import { withErrorBoundary } from "@sentry/nextjs";
import { FC } from "react";
import styled from "styled-components";
import { Markdown, ArrowLink } from "components";
import { TickIcon } from "icons/TickIcon";
import { Features25Content } from "types/graphql/generated";

interface Props {
  className?: string;
  title: Features25Content["title"];
  description: Features25Content["description"];
  linkLabel: Features25Content["features25label"];
  link: Features25Content["features25link"];
  featuresList: Features25Content["features25list"];
}

const TextContainer: FC<Props> = withErrorBoundary(
  ({ className = "", title, description, link, linkLabel, featuresList }) => {
    return (
      <StyledTextContainer className={className}>
        <h2 className="Features25__title Font_48_54_600 lg:Font_40_48_600 sm:Font_32_40_600">{title}</h2>
        <Markdown className="Features25__description Color_secondary Font_24_32_450">{description}</Markdown>
        <ul className="Features25__featuresList">
          {featuresList.map(({ element, id }) => (
            <li key={id} className="Features25__feature">
              <TickIcon className="Features25__tickIcon" />
              <Markdown className="Features25__featureInfo Font_24_32_600 sm:Font_18_26_600">{element}</Markdown>
            </li>
          ))}
        </ul>
        <ArrowLink href={link} className="Features25__link Font_18_26_600 sm: Font_34_41_600">
          {linkLabel}
        </ArrowLink>
      </StyledTextContainer>
    );
  },
  {}
);

const StyledTextContainer = styled.div`
  .Features25__title {
    max-width: 549px;
  }

  .Features25__description {
    margin-top: 24px;
  }

  .Features25__featuresList {
    margin-top: 40px;
  }

  .Features25__feature {
    display: flex;
    align-items: center;
  }

  .Features25__feature:not(:first-child) {
    margin-top: 16px;
  }

  .Features25__tickIcon {
    position: relative;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .Features25__featureInfo {
    margin-left: 16px;
  }

  .Features25__link {
    margin-top: 40px;

    svg {
      width: 12px;
      height: 12px;
      margin-top: 2px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Features25__title {
      width: 522px;
    }

    .Features25__featuresList {
      max-width: 428px;
    }

    .Features25__feature {
      align-items: flex-start;
    }

    .Features25__tickIcon {
      margin-top: 3px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Features25__title {
      max-width: unset;
      width: fit-content;
    }

    .Features25__featuresList {
      max-width: unset;
    }

    .Features25__feature {
      max-width: unset;
    }
  }
`;

export { TextContainer };
